import { useState } from "react";
import axios from "axios";
import { Edit2, Save, X, Loader2, ExternalLink, Check } from "lucide-react";
import PhotoGallery from "./PhotoGallery";
import RichTextField from "./RichTextField";
import SessionLocationField from "./SessionLocationField";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLACEHOLDER_PHOTO = "https://images.pexels.com/photos/7715823/pexels-photo-7715823.jpeg?auto=compress&cs=tinysrgb&w=400";

function getProfilePhoto(photos) {
  if (!photos?.length) return null;
  const p = photos[0];
  return p.thumbnails?.large?.url || p.thumbnails?.full?.url || p.url || null;
}

function computeCompleteness(profile) {
  if (!profile) return { pct: 0, missing: [] };
  const checks = [
    ["Full name", profile.full_name],
    ["Band / act name", profile.band_name],
    ["Mobile number", profile.mobile_number],
    ["Delivery address", profile.delivery_address],
    ["Billing address", profile.billing_address],
    ["Biography", profile.brief_biography],
    ["Feature video", profile.feature_video_url],
    ["Base country & region", profile.base_country_region],
    ["Session intro text", profile.session_intro_text],
    ["Genres / skill set", profile.genres_skillset],
    ["Session services", (profile.session_services_offered || []).length > 0],
    ["Profile photos", (profile.photos || []).length > 0],
    ["Stick art", (profile.stick_art || []).length > 0],
  ];
  const isFilled = (v) =>
    typeof v === "boolean" ? v : !!(v && String(v).trim());
  const missing = checks.filter(([, v]) => !isFilled(v)).map(([label]) => label);
  const filled = checks.length - missing.length;
  return { pct: Math.round((filled / checks.length) * 100), missing };
}

function ProgressMeter({ profile, testId }) {
  const { pct, missing } = computeCompleteness(profile);
  const complete = pct === 100;
  return (
    <div className="progress-meter" data-testid={testId}>
      <div className="progress-meter-head">
        <span className="progress-meter-label">PROFILE COMPLETENESS</span>
        <span className="progress-meter-pct" data-testid={`${testId}-pct`}>{pct}%</span>
      </div>
      <div className="progress-track">
        <div className={`progress-fill ${complete ? "progress-complete" : ""}`} style={{ width: `${pct}%` }} />
      </div>
      {complete ? (
        <p className="progress-hint progress-hint-done">All done — your profile is fully complete.</p>
      ) : (
        <p className="progress-hint">
          Add {missing.slice(0, 3).join(", ")}
          {missing.length > 3 ? ` +${missing.length - 3} more` : ""} to complete your profile.
        </p>
      )}
    </div>
  );
}

function InfoCard({ title, children, editMode, onEdit, onSave, onCancel, saving, testId }) {
  return (
    <div className="info-card" data-testid={testId}>
      <div className="info-card-header">
        <h3 className="info-card-title">{title}</h3>
        {!editMode ? (
          <button className="btn-edit" onClick={onEdit} data-testid={`edit-${testId}`}>
            <Edit2 size={14} /> EDIT
          </button>
        ) : (
          <div className="edit-actions">
            <button className="btn-save" onClick={onSave} disabled={saving} data-testid={`save-${testId}`}>
              {saving ? <Loader2 size={14} className="spin" /> : <Save size={14} />}
              {saving ? "SAVING..." : "SAVE"}
            </button>
            <button className="btn-cancel" onClick={onCancel} data-testid={`cancel-${testId}`}>
              <X size={14} /> CANCEL
            </button>
          </div>
        )}
      </div>
      <div className="info-card-body">{children}</div>
    </div>
  );
}

function Field({ label, value, editMode, onChange, type = "text", placeholder, testId }) {
  return (
    <div className="field-group">
      <label className="field-label">{label}</label>
      {editMode ? (
        type === "textarea" ? (
          <textarea
            className="field-input field-textarea"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            rows={4}
            data-testid={testId}
          />
        ) : (
          <input
            type={type}
            className="field-input"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            data-testid={testId}
          />
        )
      ) : (
        <div className="field-value" data-testid={`display-${testId}`}>
          {value || <span className="field-empty">Not set</span>}
        </div>
      )}
    </div>
  );
}

export default function ProfilePanel({ profile, token, panelIndex, onUpdate, servicesOptions = [] }) {
  const [editingSection, setEditingSection] = useState(null);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({});

  const authHeader = { Authorization: `Bearer ${token}` };
  const profilePhoto = getProfilePhoto(profile?.photos);
  const pid = `p${panelIndex}`;

  const startEdit = (section) => {
    setEditingSection(section);
    setDraft({ ...profile });
  };

  const cancelEdit = () => {
    setEditingSection(null);
    setDraft({});
  };

  const saveSection = async (section) => {
    setSaving(true);
    try {
      const sectionFields = {
        personal: ["full_name", "band_name", "mobile_number"],
        address: ["delivery_address", "billing_address"],
        bio: ["brief_biography"],
        video: ["feature_video_url"],
        social: ["website_url", "facebook_url", "instagram_url", "youtube_url", "spotify_url"],
        session: ["hireable_for_sessions", "session_services_offered", "session_intro_text", "base_country_region", "genres_skillset"],
      };
      const payload = { record_id: profile.record_id };
      (sectionFields[section] || []).forEach((key) => {
        if (draft[key] !== undefined) payload[key] = draft[key];
      });

      const { data } = await axios.patch(`${API}/artist/profile`, payload, {
        headers: { ...authHeader, "Content-Type": "application/json" },
      });
      onUpdate(panelIndex, data.profile);
      setEditingSection(null);
      setDraft({});
    } catch (err) {
      alert(err.response?.data?.detail || "Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleGalleryUpdate = (updatedProfile) => {
    onUpdate(panelIndex, updatedProfile);
  };

  const updateDraft = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));

  const val = (key) => editingSection ? draft[key] : profile?.[key];

  return (
    <div className="profile-panel" data-testid={`profile-panel-${panelIndex}`}>

      {/* Panel Mini-Hero */}
      <div className="panel-hero">
        <div className="panel-photo-wrap">
          <img
            src={profilePhoto || PLACEHOLDER_PHOTO}
            alt={profile?.full_name || "Artist"}
            className="panel-photo"
            data-testid={`panel-photo-${panelIndex}`}
          />
          <div className="panel-photo-border" />
        </div>
        <div className="panel-hero-info">
          <h2 className="panel-name" data-testid={`panel-name-${panelIndex}`}>
            {profile?.full_name || "YOUR NAME"}
          </h2>
          <div className="hero-divider" />
          <p className="panel-band" data-testid={`panel-band-${panelIndex}`}>
            {profile?.band_name || "YOUR BAND"}
          </p>
        </div>
      </div>

      {/* Completeness meter */}
      <ProgressMeter profile={profile} testId={`progress-${pid}`} />

      {/* Personal Info */}
      <InfoCard
        title="PERSONAL INFO"
        testId={`personal-${pid}`}
        editMode={editingSection === "personal"}
        onEdit={() => startEdit("personal")}
        onSave={() => saveSection("personal")}
        onCancel={cancelEdit}
        saving={saving}
      >
        <Field label="FULL NAME" value={val("full_name")} editMode={editingSection === "personal"} onChange={(v) => updateDraft("full_name", v)} testId={`${pid}-full-name`} />
        <Field label="BAND / ACT NAME" value={val("band_name")} editMode={editingSection === "personal"} onChange={(v) => updateDraft("band_name", v)} testId={`${pid}-band-name`} />
        <Field label="MOBILE NUMBER" value={val("mobile_number")} editMode={editingSection === "personal"} onChange={(v) => updateDraft("mobile_number", v)} type="tel" testId={`${pid}-mobile`} />
        <Field label="EMAIL ADDRESS" value={profile?.email_address} editMode={false} testId={`${pid}-email`} />
      </InfoCard>

      {/* Session Database */}
      <InfoCard
        title="SESSION DATABASE"
        testId={`session-${pid}`}
        editMode={editingSection === "session"}
        onEdit={() => startEdit("session")}
        onSave={() => saveSection("session")}
        onCancel={cancelEdit}
        saving={saving}
      >
        <p className="session-help">
          Manage your listing in our online session-musician database.
        </p>

        {/* Hireable checkbox */}
        <div className="field-group">
          <label className="field-label">HIREABLE FOR SESSIONS?</label>
          {editingSection === "session" ? (
            <label className="checkbox-row" data-testid={`${pid}-hireable-toggle`}>
              <input
                type="checkbox"
                checked={!!draft.hireable_for_sessions}
                onChange={(e) => updateDraft("hireable_for_sessions", e.target.checked)}
              />
              <span>
                Tick to publish yourself in our online session database
              </span>
            </label>
          ) : (
            <div className="field-value" data-testid={`display-${pid}-hireable`}>
              {profile?.hireable_for_sessions ? (
                <span className="pill pill-yes"><Check size={12} /> YES — LISTED</span>
              ) : (
                <span className="pill pill-no">NOT LISTED</span>
              )}
            </div>
          )}
        </div>

        {/* Session Services Offered */}
        <div className="field-group">
          <label className="field-label">SESSION SERVICES OFFERED</label>
          {editingSection === "session" ? (
            <div className="chip-picker" data-testid={`${pid}-services-picker`}>
              {servicesOptions.length === 0 && (
                <span className="field-empty">No options configured in Airtable yet.</span>
              )}
              {servicesOptions.map((opt) => {
                const selected = (draft.session_services_offered || []).includes(opt);
                return (
                  <button
                    type="button"
                    key={opt}
                    className={`chip ${selected ? "chip-selected" : ""}`}
                    onClick={() => {
                      const current = draft.session_services_offered || [];
                      updateDraft(
                        "session_services_offered",
                        selected ? current.filter((s) => s !== opt) : [...current, opt]
                      );
                    }}
                    data-testid={`${pid}-service-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    {selected && <Check size={12} />} {opt}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="field-value" data-testid={`display-${pid}-services`}>
              {profile?.session_services_offered?.length ? (
                <div className="chip-list">
                  {profile.session_services_offered.map((s) => (
                    <span key={s} className="chip chip-static">{s}</span>
                  ))}
                </div>
              ) : (
                <span className="field-empty">Not set</span>
              )}
            </div>
          )}
        </div>

        <RichTextField
          label="SESSION-FOCUSED INTRO TEXT & GEAR RUN-DOWN"
          value={val("session_intro_text")}
          editMode={editingSection === "session"}
          onChange={(v) => updateDraft("session_intro_text", v)}
          placeholder="A short session-focused intro and your gear run-down…"
          testId={`${pid}-session-intro`}
        />

        <SessionLocationField
          label="BASE COUNTRY & REGION/STATE"
          value={val("base_country_region")}
          editMode={editingSection === "session"}
          onChange={(v) => updateDraft("base_country_region", v)}
          testId={`${pid}-base-region`}
        />

        <RichTextField
          label="GENRES COVERED / SKILL SET"
          value={val("genres_skillset")}
          editMode={editingSection === "session"}
          onChange={(v) => updateDraft("genres_skillset", v)}
          placeholder="Genres you cover and your key skills…"
          testId={`${pid}-genres-skillset`}
        />
      </InfoCard>

      {/* Addresses */}
      <InfoCard
        title="ADDRESSES"
        testId={`address-${pid}`}
        editMode={editingSection === "address"}
        onEdit={() => startEdit("address")}
        onSave={() => saveSection("address")}
        onCancel={cancelEdit}
        saving={saving}
      >
        <Field label="DELIVERY ADDRESS" value={val("delivery_address")} editMode={editingSection === "address"} onChange={(v) => updateDraft("delivery_address", v)} type="textarea" testId={`${pid}-delivery`} />
        <Field label="BILLING ADDRESS" value={val("billing_address")} editMode={editingSection === "address"} onChange={(v) => updateDraft("billing_address", v)} type="textarea" testId={`${pid}-billing`} />
      </InfoCard>

      {/* Biography */}
      <InfoCard
        title="BIOGRAPHY"
        testId={`bio-${pid}`}
        editMode={editingSection === "bio"}
        onEdit={() => startEdit("bio")}
        onSave={() => saveSection("bio")}
        onCancel={cancelEdit}
        saving={saving}
      >
        <Field label="BRIEF BIOGRAPHY" value={val("brief_biography")} editMode={editingSection === "bio"} onChange={(v) => updateDraft("brief_biography", v)} type="textarea" testId={`${pid}-biography`} />
      </InfoCard>

      {/* Feature Video */}
      <InfoCard
        title="FEATURE VIDEO"
        testId={`video-${pid}`}
        editMode={editingSection === "video"}
        onEdit={() => startEdit("video")}
        onSave={() => saveSection("video")}
        onCancel={cancelEdit}
        saving={saving}
      >
        <Field label="YOUTUBE / VIMEO URL" value={val("feature_video_url")} editMode={editingSection === "video"} onChange={(v) => updateDraft("feature_video_url", v)} type="url" testId={`${pid}-video-url`} />
        {profile?.feature_video_url && editingSection !== "video" && (
          <a href={profile.feature_video_url} target="_blank" rel="noopener noreferrer" className="video-link">
            <ExternalLink size={14} /> VIEW VIDEO
          </a>
        )}
      </InfoCard>

      {/* Social & Links */}
      <InfoCard
        title="SOCIAL & LINKS"
        testId={`social-${pid}`}
        editMode={editingSection === "social"}
        onEdit={() => startEdit("social")}
        onSave={() => saveSection("social")}
        onCancel={cancelEdit}
        saving={saving}
      >
        <p className="session-help">These links appear on your public profile at londondrumsticks.co.uk.</p>
        <Field label="WEBSITE URL" value={val("website_url")} editMode={editingSection === "social"} onChange={(v) => updateDraft("website_url", v)} type="url" placeholder="https://" testId={`${pid}-website`} />
        <Field label="FACEBOOK URL" value={val("facebook_url")} editMode={editingSection === "social"} onChange={(v) => updateDraft("facebook_url", v)} type="url" placeholder="https://facebook.com/..." testId={`${pid}-facebook`} />
        <Field label="INSTAGRAM URL" value={val("instagram_url")} editMode={editingSection === "social"} onChange={(v) => updateDraft("instagram_url", v)} type="url" placeholder="https://instagram.com/..." testId={`${pid}-instagram`} />
        <Field label="YOUTUBE URL" value={val("youtube_url")} editMode={editingSection === "social"} onChange={(v) => updateDraft("youtube_url", v)} type="url" placeholder="https://youtube.com/..." testId={`${pid}-youtube`} />
        <Field label="SPOTIFY URL" value={val("spotify_url")} editMode={editingSection === "social"} onChange={(v) => updateDraft("spotify_url", v)} type="url" placeholder="https://open.spotify.com/..." testId={`${pid}-spotify`} />
      </InfoCard>

      {/* Photos */}
      <PhotoGallery
        photos={profile?.photos || []}
        fieldType="photos"
        title="PROFILE PHOTOS"
        token={token}
        recordId={profile?.record_id}
        onUpdate={handleGalleryUpdate}
      />

      {/* Stick Art */}
      <PhotoGallery
        photos={profile?.stick_art || []}
        fieldType="stickart"
        title="STICK ART"
        token={token}
        recordId={profile?.record_id}
        onUpdate={handleGalleryUpdate}
      />
    </div>
  );
}
